import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useGeo } from "@/lib/useGeo";
import { toast } from "sonner";
import { Camera, X, AlertTriangle, ShieldCheck } from "lucide-react";
import CityAutocomplete from "@/components/CityAutocomplete";
import SmartTextInput from "@/components/SmartTextInput";

const URGENCIES = [
  { v: "emergency", label: "Emergency" },
  { v: "high", label: "High" },
  { v: "normal", label: "Normal" },
  { v: "low", label: "Low" },
];

const asDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function SubmitCause() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const { geo } = useGeo();
  const [searchParams] = useSearchParams();

  const [cats, setCats] = useState({ categories: [], help_types: [], urgency_levels: [] });
  const [form, setForm] = useState({
    category: "housing",
    subcategory: "",
    title: "",
    description: "",
    photos: [],
    video_links: ["", ""],
    social_links: { facebook: "", instagram: "", youtube: "", tiktok: "", twitter: "", website: "" },
    location: { country: "", state: "", city: "", street: "", zip: "", lat: null, lng: null },
    help_types: [],
    urgency: "normal",
    privacy: { hide_phone: false, hide_email: false, verified_only: false, anonymous: false, hide_location: false },
    medical_acknowledged: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/causes/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!geo) return;
    setForm((f) => ({
      ...f,
      location: {
        ...f.location,
        country: f.location.country || geo.country_name || "",
        city: f.location.city || geo.city || "",
      },
    }));
  }, [geo]);

  // Prefill city from `?city=` (e.g. arriving from the LocationArticle
  // "Post here" FAB or the Community Hub empty-state CTA).  Wins over
  // the geolocation default because the user has expressed intent for
  // that specific city.
  useEffect(() => {
    const cityFromQuery = searchParams.get("city");
    if (cityFromQuery) {
      setForm((f) => ({
        ...f,
        location: { ...f.location, city: cityFromQuery },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subs = useMemo(() => {
    const c = cats.categories.find((x) => x.key === form.category);
    return c?.subcategories || [];
  }, [cats, form.category]);

  useEffect(() => {
    if (subs.length && !subs.includes(form.subcategory)) {
      setForm((f) => ({ ...f, subcategory: subs[0] }));
    }
  }, [subs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-elevate urgency to "high" when user picks Accidents & Disasters
  useEffect(() => {
    if (form.category === "accidents_disasters" && form.urgency === "normal") {
      setForm((f) => ({ ...f, urgency: "high" }));
    }
  }, [form.category]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="max-w-[820px] mx-auto px-6 py-24 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/submit-cause" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLoc = (k) => (e) => setForm({ ...form, location: { ...form.location, [k]: e.target.value } });
  const setSoc = (k) => (e) => setForm({ ...form, social_links: { ...form.social_links, [k]: e.target.value } });
  const togglePriv = (k) => (e) => setForm({ ...form, privacy: { ...form.privacy, [k]: e.target.checked } });
  const toggleHelp = (t) => () => setForm((f) => ({ ...f, help_types: f.help_types.includes(t) ? f.help_types.filter((x) => x !== t) : [...f.help_types, t] }));
  const setVideo = (i) => (e) => setForm({ ...form, video_links: form.video_links.map((v, ix) => ix === i ? e.target.value : v) });

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 10 - form.photos.length;
    if (files.length > remaining) toast.info(`Only ${remaining} more photos allowed`);
    const chosen = files.slice(0, remaining);
    for (const f of chosen) {
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} is over 4 MB — skipped`); continue; }
      try {
        const url = await asDataUrl(f);
        setForm((prev) => ({ ...prev, photos: [...prev.photos, url] }));
      } catch { /* ignore */ }
    }
    e.target.value = "";
  };
  const removePhoto = (i) => setForm({ ...form, photos: form.photos.filter((_, ix) => ix !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || form.title.length > 120) return toast.error("Title required (max 120 chars)");
    if (!form.description.trim() || form.description.length > 5000) return toast.error("Description required (max 5000 chars)");
    if (!form.location.country.trim() || !form.location.city.trim()) return toast.error("Country and City are required");
    if (form.category === "medical" && !form.medical_acknowledged) return toast.error("Please acknowledge the medical privacy notice");
    setBusy(true);
    try {
      const payload = {
        ...form,
        video_links: form.video_links.filter((v) => v && v.trim()),
      };
      const { data } = await http.post("/causes", payload);
      const bypass = data.status === "published";
      toast.success(bypass ? "Published — your cause is live." : "Submitted — awaiting admin review.");
      nav(`/dashboard?tab=causes`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const isMedical = form.category === "medical";

  return (
    <div data-testid="submit-cause-page" className="max-w-[820px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">SUBMIT A CAUSE / PLEA</div>
      <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">Ask for the help you need.</h1>
      <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
        Every submission is reviewed by our moderators before being published, unless posted by an official Social Pulse account. Your privacy is protected — we only display City, State, and Country publicly.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-8">
        {/* Section 1 — Category */}
        <fieldset className="wofy-card">
          <legend className="overline">1 · CATEGORY</legend>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider">Main category</span>
              <select data-testid="cause-category" value={form.category} onChange={set("category")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
                {cats.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider">Subcategory</span>
              <select data-testid="cause-subcategory" value={form.subcategory} onChange={set("subcategory")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }}>
                {subs.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
        </fieldset>

        {/* Medical notice */}
        {isMedical && (
          <div data-testid="medical-notice" className="rounded-2xl border-l-4 p-5" style={{ borderColor: "#B23A3A", background: "rgba(178,58,58,0.05)" }}>
            <div className="flex items-center gap-2 overline" style={{ color: "#B23A3A" }}>
              <AlertTriangle className="w-4 h-4" /> PRIVACY NOTICE
            </div>
            <p className="mt-2 text-sm">
              Do <b>NOT</b> upload medical records, prescriptions, insurance documents, laboratory reports, Social Security numbers, or other sensitive personal health information.
              Only describe the type of assistance you need, such as transportation, prescription assistance, home care, or medical equipment.
              Social Pulse is a community assistance platform and is not intended for storing protected medical records.
            </p>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input
                data-testid="cause-medical-ack"
                type="checkbox"
                checked={form.medical_acknowledged}
                onChange={(e) => setForm({ ...form, medical_acknowledged: e.target.checked })}
              />
              I understand and agree.
            </label>
          </div>
        )}

        {/* Section 2 — Cause details */}
        <fieldset className="wofy-card">
          <legend className="overline">2 · CAUSE DETAILS</legend>
          <label className="block mt-2">
            <span className="text-xs font-mono uppercase tracking-wider">Title <span className="opacity-60">({form.title.length}/120)</span></span>
            <input data-testid="cause-title" required maxLength={120} value={form.title} onChange={set("title")} className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </label>
          <label className="block mt-4">
            <span className="text-xs font-mono uppercase tracking-wider">Description <span className="opacity-60">({form.description.length}/5000)</span></span>
            <div className="mt-2">
              <SmartTextInput
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                rows={8}
                maxLength={5000}
                testid="cause-description"
              />
            </div>
          </label>
          <label className="block mt-4">
            <span className="text-xs font-mono uppercase tracking-wider">Add a Website Link (optional)</span>
            <input
              data-testid="cause-website"
              type="url"
              placeholder="https://…"
              value={form.social_links.website}
              onChange={setSoc("website")}
              className="w-full mt-2 rounded-lg border px-4 py-3 bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
            />
            <span className="mt-1 block text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
              A personal website, GoFundMe, or news article that supports your cause.
            </span>
          </label>
        </fieldset>

        {/* Section 3 — Photos */}
        <fieldset className="wofy-card">
          <legend className="overline">3 · PHOTOS <span className="opacity-60">(optional · max 10 · 4 MB each)</span></legend>
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
            {form.photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-white/85 text-[#14213D] border border-[#14213D]/20 rounded-full p-1"><X className="w-3 h-3" /></button>
              </div>
            ))}
            {form.photos.length < 10 && (
              <label className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:opacity-80" style={{ borderColor: "var(--wofy-border)" }} data-testid="cause-add-photo">
                <Camera className="w-6 h-6" style={{ color: "var(--wofy-text-soft)" }} />
                <span className="text-[10px] mt-1" style={{ color: "var(--wofy-text-soft)" }}>Add</span>
                <input type="file" multiple accept="image/*" className="hidden" onChange={onPhotos} />
              </label>
            )}
          </div>
          <div className="mt-4">
            <span className="text-xs font-mono uppercase tracking-wider">Video links (YouTube / Vimeo, max 2)</span>
            <input placeholder="https://youtube.com/…" value={form.video_links[0]} onChange={setVideo(0)} className="w-full mt-2 rounded-lg border px-4 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
            <input placeholder="https://youtube.com/… (optional)" value={form.video_links[1]} onChange={setVideo(1)} className="w-full mt-2 rounded-lg border px-4 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
          </div>
        </fieldset>

        {/* Section 4 — Social */}
        <fieldset className="wofy-card">
          <legend className="overline">4 · SOCIAL LINKS (optional)</legend>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {[
              ["facebook", "Facebook"], ["instagram", "Instagram"], ["youtube", "YouTube"],
              ["tiktok", "TikTok"], ["twitter", "X (Twitter)"],
            ].map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-xs font-mono uppercase tracking-wider">{label}</span>
                <input value={form.social_links[k]} onChange={setSoc(k)} placeholder="https://…" className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} />
              </label>
            ))}
          </div>
        </fieldset>

        {/* Section 5 — Location */}
        <fieldset className="wofy-card">
          <legend className="overline">5 · LOCATION</legend>
          <p className="text-[11px] mt-2" style={{ color: "var(--wofy-text-soft)" }}>Only City, State, and Country are shown publicly. Street address is never revealed.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <label className="block"><span className="text-xs font-mono uppercase tracking-wider">Country *</span><input required value={form.location.country} onChange={setLoc("country")} className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid="cause-country" /></label>
            <label className="block"><span className="text-xs font-mono uppercase tracking-wider">State / Province</span><input value={form.location.state} onChange={setLoc("state")} className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} /></label>
            <div className="block">
              <CityAutocomplete
                countryCode={undefined /* free-text country field here, no ISO code available */}
                value={form.location.city}
                onChange={(v) => setForm({ ...form, location: { ...form.location, city: v } })}
                onSelect={(picked) => setForm({
                  ...form,
                  location: {
                    ...form.location,
                    city: picked.city || form.location.city,
                    state: form.location.state || picked.state || "",
                    country: form.location.country || picked.country || "",
                    lat: picked.lat ?? form.location.lat,
                    lng: picked.lng ?? form.location.lng,
                  },
                })}
                testid="cause-city-ac"
              />
            </div>
            <label className="block"><span className="text-xs font-mono uppercase tracking-wider">Zip / postal (optional)</span><input value={form.location.zip} onChange={setLoc("zip")} className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} /></label>
            <label className="block sm:col-span-2"><span className="text-xs font-mono uppercase tracking-wider">Street address (optional · private)</span><input value={form.location.street} onChange={setLoc("street")} className="w-full mt-2 rounded-lg border px-3 py-2 bg-transparent" style={{ borderColor: "var(--wofy-border)" }} /></label>
          </div>
        </fieldset>

        {/* Section 7 — Help types */}
        <fieldset className="wofy-card">
          <legend className="overline">6 · TYPE OF HELP NEEDED</legend>
          <div className="grid sm:grid-cols-3 gap-2 mt-3">
            {cats.help_types.map((t) => (
              <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.help_types.includes(t)} onChange={toggleHelp(t)} data-testid={`cause-help-${t.toLowerCase().replace(/\s+/g,'-')}`} />
                {t}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Section 8 — Urgency */}
        <fieldset className="wofy-card">
          <legend className="overline">7 · URGENCY</legend>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {URGENCIES.map((u) => (
              <label key={u.v} className={`cursor-pointer text-center py-2 rounded-lg border text-sm font-medium ${form.urgency === u.v ? "text-white" : ""}`}
                style={form.urgency === u.v
                  ? { background: u.v === "emergency" ? "#B23A3A" : u.v === "high" ? "#B25D38" : u.v === "normal" ? "var(--wofy-primary)" : "#7A7A7A", borderColor: "transparent" }
                  : { borderColor: "var(--wofy-border)" }}>
                <input type="radio" name="urgency" value={u.v} checked={form.urgency === u.v} onChange={set("urgency")} className="sr-only" />
                {u.label}
              </label>
            ))}
          </div>
        </fieldset>

        {/* Section 9 — Privacy */}
        <fieldset className="wofy-card">
          <legend className="overline">8 · PRIVACY</legend>
          <div className="mt-3 space-y-2 text-sm">
            {[
              ["hide_phone", "Hide my phone number"],
              ["hide_email", "Hide my email"],
              ["verified_only", "Only verified volunteers may contact me"],
              ["anonymous", "Publish anonymously"],
              ["hide_location", "Hide exact location (show country only)"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.privacy[k]} onChange={togglePriv(k)} data-testid={`cause-privacy-${k}`} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button type="submit" disabled={busy || (isMedical && !form.medical_acknowledged)} data-testid="cause-submit" className="wofy-btn-primary disabled:opacity-60">
            <ShieldCheck className="w-4 h-4" /> {busy ? "Submitting…" : "Submit for review"}
          </button>
          <span className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            You'll be notified by email and in-app when a moderator reviews your submission.
          </span>
        </div>
      </form>
    </div>
  );
}
